import React from 'react';
import { useFluoVehicles } from '../hooks/useFluoVehicles';

/**
 * Single-block pictogram on the Fluo brand dark-blue background.
 *  - When `showLogo` is true (default), renders "Train" + the green Fluo wordmark.
 *  - When `showLogo` is false, renders only the label so non-Fluo operators
 *    (TGV SNCF, DB, etc. — 4-digit train numbers) don't get the Fluo branding.
 *
 * `size`  controls the overall scale (sm = table rows, md / lg = headings).
 * `label` lets callers swap "Train" → "trains" (plural in the InfoFluo title).
 * `number` enables car/train image badge when fluo_vehicles.json is loaded.
 */
const TrainFluoBadge = ({
  size = 'sm',
  label = 'Train',
  showLogo = true,
  className = '',
  testId = 'train-fluo-badge',
  number = null,
}) => {
  const { getFluoBadgeUrl, isFluoCar, loaded } = useFluoVehicles();
  const [imgErr, setImgErr] = React.useState(false);

  // Text font-size = logo height so the two read at the same optical height.
  const sizes = {
    sm: { pad: 'px-2 py-1', text: 'text-[12px]', logo: 12, gap: 'gap-1.5' },
    md: { pad: 'px-2.5 py-1', text: 'text-[15px]', logo: 15, gap: 'gap-2' },
    lg: { pad: 'px-3 py-1.5', text: 'text-[22px]', logo: 22, gap: 'gap-2' },
  }[size] || { pad: 'px-2 py-1', text: 'text-[12px]', logo: 12, gap: 'gap-1.5' };

  if (loaded && number && !imgErr) {
    const badgeUrl = getFluoBadgeUrl(number);
    if (badgeUrl) {
      return (
        <img
          data-testid={testId}
          src={badgeUrl}
          alt={isFluoCar(number) ? 'Car Fluo' : 'Train Fluo'}
          className={`h-5 object-contain ${className}`}
          onError={() => setImgErr(true)}
        />
      );
    }
  }

  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center shrink-0 leading-none ${sizes.gap} rounded font-bold tracking-wide bg-ter-blue text-white ${sizes.pad} ${sizes.text} ${className}`}
    >
      <span className="leading-none">{label}</span>
      {showLogo && (
        <img
          src="/assets/fluo_green.png"
          alt="Fluo"
          style={{ height: sizes.logo, width: 'auto' }}
          className="object-contain shrink-0 block"
        />
      )}
    </span>
  );
};

export default TrainFluoBadge;
